import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Fade,
  Paper,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { HandymanAppState } from "../../configuration/redux/reducers";
import { getUserReviews, createReview, submitReview } from "./actions";
import { ReviewType, CreateReviewCommand } from "../../configuration/api/apiSchema";
import ReviewsList from "../../components/Reviews/ReviewsList";
import RatingSummary from "../../components/Reviews/RatingSummary";
import CreateReviewForm from "../../components/Reviews/CreateReviewForm";

const UserReviewsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: HandymanAppState) => state.user.currentUserDetails);
  const { userReviews, userReviewsLoading, userReviewsError, userReviewsTotalCount } = useSelector(
    (state: HandymanAppState) => state.review
  );
  
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState(0);
  const [createReviewDialogOpen, setCreateReviewDialogOpen] = useState(false);

  useEffect(() => {
    if (userId) {
      dispatch(getUserReviews(userId, currentPage) as any);
    }
  }, [dispatch, userId, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleOpenCreateReviewDialog = () => {
    setCreateReviewDialogOpen(true);
  };

  const handleCloseCreateReviewDialog = () => {
    setCreateReviewDialogOpen(false);
  };

  const handleSubmitReview = async (data: CreateReviewCommand) => {
    await dispatch(createReview(data) as any);
    handleCloseCreateReviewDialog();
    // Refresh reviews
    if (userId) {
      dispatch(getUserReviews(userId, currentPage) as any);
    }
  };

  const handleRespondToReview = async (reviewId: string, response: string) => {
    await dispatch(submitReview(reviewId, { reviewId, languageKey: "en" }) as any);
    // Refresh reviews
    if (userId) {
      dispatch(getUserReviews(userId, currentPage) as any);
    }
  };

  // Filter reviews based on active tab
  const filteredReviews = userReviews.filter(review => {
    if (activeTab === 0) return true; // All reviews
    if (activeTab === 1) return review.type === ReviewType.CustomerToHandyman; // As Handyman
    if (activeTab === 2) return review.type === ReviewType.HandymanToCustomer; // As Customer
    return true;
  });

  // Determine if current user can leave a review for this user
  const canLeaveReview = currentUser && userId && currentUser.id !== userId;

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #f5f5f5, #ffffff)",
        minHeight: "100vh",
        py: 10,
      }}
    >
      <Box
        width={{ xs: "90%", sm: "80%", md: "75%" }}
        mx="auto"
        mt={4}
        p={{ xs: 2, sm: 4 }}
        component={Paper}
        elevation={4}
        sx={{ borderRadius: 4 }}
      >
        <Box mb={4}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" gutterBottom>
            User Reviews
          </Typography>
        </Box>

        <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={4}>
          {/* Left column: Rating summary */}
          <Box width={{ xs: "100%", md: "35%" }}>
            <RatingSummary
              reviews={userReviews}
              loading={userReviewsLoading}
              totalCount={userReviewsTotalCount}
            />

            {canLeaveReview && (
              <Fade in={true} timeout={500}>
                <Paper
                  sx={{
                    p: 3,
                    mt: 3,
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Worked with this user?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Share your experience by leaving a review.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleOpenCreateReviewDialog}
                  >
                    Leave a Review
                  </Button>
                </Paper>
              </Fade>
            )}
          </Box>

          {/* Right column: Reviews list with tabs */}
          <Box width={{ xs: "100%", md: "65%" }}>
            <Paper sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="All Reviews" />
                <Tab label="As Handyman" />
                <Tab label="As Customer" />
              </Tabs>
            </Paper>

            <ReviewsList
              reviews={filteredReviews}
              loading={userReviewsLoading}
              error={userReviewsError}
              totalCount={userReviewsTotalCount}
              onPageChange={handlePageChange}
              onRespond={handleRespondToReview}
              showActions={currentUser?.id === userId}
              emptyMessage={
                activeTab === 0
                  ? "No reviews yet"
                  : activeTab === 1
                  ? "No reviews as handyman"
                  : "No reviews as customer"
              }
              title=""
              currentPage={currentPage}
            />
          </Box>
        </Box>

        {/* Create Review Dialog */}
        <Dialog
          open={createReviewDialogOpen}
          onClose={handleCloseCreateReviewDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { height: "45vh", maxHeight: "45vh" } }}
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Write a Review</Typography>
              <IconButton onClick={handleCloseCreateReviewDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <CreateReviewForm
              orderId=""
              reviewerId={currentUser?.id || ""}
              revieweeId={userId || ""}
              reviewType={ReviewType.CustomerToHandyman}
              onSubmit={handleSubmitReview}
              onCancel={handleCloseCreateReviewDialog}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
};

export default UserReviewsPage; 